import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';

const API_URL = config.API_BASE_URL;

export interface GoogleAdsLeadIntegration {
  id: string;
  sectorId: number;
  name: string;
  enabled: boolean;
  publicId: string;
  webhookUrl: string;
  columnId?: number | null;
  assignedTo?: number | null;
  defaultPhoneCountry?: string | null;
  /** null = integração sem perfil: Google Ads só se o lead vier com dados do Google. */
  sourceType?: string | null;
  sourceLabel?: string | null;
  nameColumnId?: string | null;
  phoneColumnId?: string | null;
  emailColumnId?: string | null;
  lastTestReceivedAt?: string | null;
  lastProductionReceivedAt?: string | null;
  lastProcessedAt?: string | null;
  secretRotatedAt?: string | null;
  createdAt: string;
}

export interface GoogleAdsLeadEvent {
  id: string;
  externalLeadId: string;
  isTest: boolean;
  status: string;
  receivedAt: string;
  processedAt?: string | null;
  errorCode?: string | null;
  contactId?: number | null;
  cardId?: number | null;
  extractedName?: string | null;
  phoneOriginal?: string | null;
  phoneNormalized?: string | null;
  email?: string | null;
  leadSource?: string | null;
  leadSourceLabel: string;
  attempts: number;
  deliveryCount: number;
  /** false = telefone sem "+" e integração sem país: nunca assumimos o DDI. */
  phoneCountryKnown?: boolean;
}

export interface GoogleAdsLeadSeenColumn {
  columnId: string;
  columnName?: string | null;
}

export interface GoogleAdsLeadWrite {
  sectorId: number;
  name: string;
  enabled: boolean;
  columnId?: number | null;
  assignedTo?: number | null;
  defaultPhoneCountry?: string | null;
  sourceType?: string | null;
  sourceLabel?: string | null;
  nameColumnId?: string | null;
  phoneColumnId?: string | null;
  emailColumnId?: string | null;
}

function authHeaders() {
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${SessionService.getToken()}`,
  };
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export async function listGoogleAdsLeadIntegrations(sectorId: number): Promise<GoogleAdsLeadIntegration[]> {
  try {
    const response = await axios.get(`${API_URL}/google-ads-lead-integrations`, {
      params: { sectorId },
      headers: authHeaders(),
    });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível carregar as integrações.'));
  }
}

export async function getGoogleAdsLeadIntegration(id: string): Promise<{
  integration: GoogleAdsLeadIntegration;
  events: GoogleAdsLeadEvent[];
}> {
  try {
    const response = await axios.get(`${API_URL}/google-ads-lead-integrations/${id}`, {
      headers: authHeaders(),
    });
    return response.data?.data;
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível carregar a integração.'));
  }
}

export async function listGoogleAdsLeadColumns(id: string): Promise<GoogleAdsLeadSeenColumn[]> {
  try {
    const response = await axios.get(`${API_URL}/google-ads-lead-integrations/${id}/columns`, {
      headers: authHeaders(),
    });
    return response.data?.data ?? [];
  } catch {
    return [];
  }
}

export async function createGoogleAdsLeadIntegration(input: GoogleAdsLeadWrite): Promise<{
  integration: GoogleAdsLeadIntegration;
  webhookUrl: string;
  secret: string;
}> {
  try {
    const response = await axios.post(`${API_URL}/google-ads-lead-integrations`, input, {
      headers: authHeaders(),
    });
    return response.data?.data;
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível criar a integração.'));
  }
}

export async function updateGoogleAdsLeadIntegration(id: string, input: GoogleAdsLeadWrite): Promise<GoogleAdsLeadIntegration> {
  try {
    const response = await axios.put(`${API_URL}/google-ads-lead-integrations/${id}`, input, {
      headers: authHeaders(),
    });
    return response.data?.data;
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível salvar a integração.'));
  }
}

export async function deleteGoogleAdsLeadIntegration(id: string): Promise<void> {
  try {
    await axios.delete(`${API_URL}/google-ads-lead-integrations/${id}`, {
      headers: authHeaders(),
    });
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível excluir a integração.'));
  }
}

export async function rotateGoogleAdsLeadSecret(id: string): Promise<{ secret: string; webhookUrl: string }> {
  try {
    const response = await axios.post(`${API_URL}/google-ads-lead-integrations/${id}/rotate`, {}, {
      headers: authHeaders(),
    });
    return response.data?.data;
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível rotacionar a chave.'));
  }
}

export async function reprocessGoogleAdsLeadEvent(integrationId: string, eventId: string): Promise<void> {
  try {
    await axios.post(
      `${API_URL}/google-ads-lead-integrations/${integrationId}/events/${eventId}/reprocess`,
      {},
      { headers: authHeaders() },
    );
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível reprocessar o evento.'));
  }
}
